/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { colors, mixins } from '@blend/consumer-components';
import { Modal } from '@blend/shared-react';
import { css, StyleSheet } from 'aphrodite';
import React, { useState } from 'react';
import { getStyleConfig, styles as modalStyles } from '../../components/common/Modals/styles';
import NPS from '../../components/form/Inputs/NPS/NPS';
import { FeedbackModal } from '../../components/NotaryRoom/FeedbackModal/FeedbackModal';
import { useRootSelector } from '../../hooks/useRootSelector';
import { getConfig } from '../../utils/configs';
import { goTo } from '../../utils/routing';

const overlayStyleConfig = {
  backgroundColor: colors.catalyst,
};

export namespace NotaryRoomCompletePage {
  export interface Props {}
}

const styles = StyleSheet.create({
  contentWrapper: {
    ...mixins.margin(40, 25, 40, 25),
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
  },
  headerText: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 15,
  },
  helperText: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 10,
    textAlign: 'left',
  },
  buttonWrapper: {
    marginTop: 30,
  },
  submitDescription: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    color: colors.catalystLight80,
    ...mixins.margin(30, 0, 0, 0),
  },
  surveyWrapper: {
    ...mixins.margin(20, 50, 50, 50),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingWrapper: {
    ...mixins.margin(20, 0, 0, 0),
  },
});

export const NotaryRoomCompletePage: React.FC<NotaryRoomCompletePage.Props> = ({}) => {
  const isMobileView = useRootSelector(state => state.layout.isMobileView);
  const isFeedbackCollectionEnabled = getConfig().isFeedbackCollectionEnabled;
  const callDetails = useRootSelector(reduxState => reduxState.callDetails);
  const callId = callDetails?.id;
  const [satisfactionValue, setSatisfactionValue] = useState<number | null>(null);
  const [isFeedbackModalSubmitting, setIsFeedbackModalSubmitting] = React.useState(false);
  const [feedbackModalIsOpen, setFeedbackModalIsOpen] = useState<boolean>(false);

  const satisfactionQuestion = 'How would you rate your overall satisfaction with this online closing experience?';
  const handleRatingChange = (value: number) => {
    setSatisfactionValue(value);
    setFeedbackModalIsOpen(true);
  };

  const feedBackModal = (
    <Modal
      overlayStyleConfig={overlayStyleConfig}
      data-it="NotaryRoomCompleteWithFeedbackPage"
      isOpen
      contentLabel="NotaryFeedback"
      handleClose={() => {}}
    >
      <div className={css(styles.contentWrapper)}>
        {feedbackModalIsOpen && satisfactionValue !== null ? (
          <div>
            <FeedbackModal
              contentLabel="notary_feedback_modal"
              callId={callId}
              satisfactionScore={satisfactionValue}
              isOpen={feedbackModalIsOpen}
              onSubmitSuccess={() => goTo('/notary/journal')}
              isSubmitting={isFeedbackModalSubmitting}
              setIsSubmitting={setIsFeedbackModalSubmitting}
            />
          </div>
        ) : (
          <div>
            <div className={css(styles.headerText)}>This appointment has ended</div>
            <div className={css(styles.surveyWrapper)}>
              <div className={css(styles.helperText)}>{satisfactionQuestion}</div>
              <NPS ratingValue={null} onChange={handleRatingChange} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );

  const thankYouModal = (
    <Modal
      overlayStyleConfig={overlayStyleConfig}
      styleConfig={{ ...getStyleConfig(isMobileView), backgroundColor: colors.white, boxShadow: 'none' }}
      data-it="NotaryRoomCompletePage"
      isOpen
      contentLabel="Thank you!"
      handleClose={() => {}}
    >
      <div className={css(modalStyles.content)}>
        <h1 className={css(modalStyles.heading)}>Thank you!</h1>
        <p className={css(modalStyles.description)}>
          The settlement agent may reach out to you regarding this RON appointment.
        </p>
        <p className={css(modalStyles.description)}>You can now close this window.</p>
      </div>
    </Modal>
  );

  return isFeedbackCollectionEnabled ? feedBackModal : thankYouModal;
};
